import { OktaAuth as OktaClient } from '@okta/okta-auth-js';
import React from 'react';

/**
 * OAuth scopes used for Okta tokens.
 */
export const SCOPES = ['openid', 'email', 'profile', 'groups', 'offline_access'];

/**
 * OAuth scopes used for Okta tokens (for command-line authentication).
 * Command-line authentication does not request the `offline_access` scope because we do not want
 * to return a refresh token for command line tokens.
 */
export const CLI_SCOPES = ['openid', 'email', 'profile', 'groups'];

/**
 * Name of the cookie set in the UI at the beginning of the auth flow with a nonce value (i.e. random string) which
 * the backend later verifies at the end of the login flow.
 */
export const NONCE_COOKIE_NAME = 'chrono-auth-nonce';

/**
 * Auth configuration data that comes back from the server
 */
export interface AuthenticationConfig {
  // Determines which "client" to use when performing auth functions
  type: 'fakeauth' | 'okta';

  // Determines what capabilities are enabled
  allow_single_sign_on: boolean;
  allow_username_password: boolean;
  enable_pkce_auth_flow: boolean;
  // Configuration for the Okta auth client
  okta: {
    client_id: string;
    issuer: string;
    idp: string;
  };

  // Other tenant info returned by endpoint, but not currently used
  subdomain: string;
  tenant: string;
}

export interface User {
  id: string;
  email: string;
  isAdmin: boolean;
}

export interface AuthenticationContextValue {
  /**
   * Whether authentication information is being loaded from the server.
   */
  isLoading: boolean;

  /**
   * Auth configuration loaded from the server.
   */
  config?: AuthenticationConfig;

  /**
   * Login route
   */
  loginRoute: string;

  /**
   * Will be undefined while loading and after loading if Okta is not configured to be used for authentication.
   */
  oktaClient?: OktaClient;

  /**
   * Will be undefined while loading and after loading if Okta is not configured to be used for authentication.
   */
  oktaImpersonateClient?: OktaClient;

  /**
   * Will be undefined while loading and after loading if Okta is not configured to be used for authentication.
   */
  oktaCliClient?: OktaClient;

  /**
   * Will be undefined while loading and after loading if Okta is not configured to be used for authentication.
   */
  oktaCliImpersonateClient?: OktaClient;

  /**
   * Auth configuration loaded from the server.
   */
  oktaImpersonateConfig?: AuthenticationConfig;

  /**
   * The currently logged in user or undefined if not logged in once isLoading is false.
   */
  currentUser?: User;

  /**
   * Ask for the current user to be refetched from the server.
   */
  refreshCurrentUser?: () => void;

  /**
   * Refresh the auth token.
   */
  refreshAuthToken: () => Promise<void>;
}

export const AuthenticationContext = React.createContext<AuthenticationContextValue>({
  isLoading: false,
  config: undefined,
  oktaClient: undefined,
  oktaImpersonateClient: undefined,
  oktaCliClient: undefined,
  oktaCliImpersonateClient: undefined,
  currentUser: undefined,
  refreshCurrentUser: undefined,
  refreshAuthToken: () => Promise.resolve(),
  loginRoute: '',
});
